import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import campaignCopyJson from "src/app/features/Campaign/components/forms/campaign-copy.json";
import { FormManager } from "../../../../../components/Form/FormManager";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { FormJson } from "../../../../../core/types/FormJsonTypes";
import { useCampaignCopyMutation } from "../../services/server/CampaignApi";
import type { CampaignCopyEntity } from "../../types/CampaignCopyEntity";

export const CampaignCopyForm: FC<FormProps> = ({ runSubmit }) => {
    const formDefinition = campaignCopyJson as FormJson;

    const [formData, setFormData] = useState<CampaignCopyEntity>({} as CampaignCopyEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCampaignCopyMutation();

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                const sites1 = result.data?.sites1;
                const isTerminals = !sites1 || (Array.isArray(sites1) && sites1.length === 0);
                const variant = isTerminals ? ResourceVariant.TERMINALS : ResourceVariant.ORG_UNITS;

                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formCopySuccess", {
                            id: result.data.id,
                            resource: "Campaign",
                        }),
                        autoCloseDisabled: false,
                    }),
                );

                navigate(`${BASE_PATH}/${ResourceName.CAMPAIGNS}/${variant}/${FORM_ACTION.EDIT}/${result.data.id}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    const { id } = useParams();
    return (
        <Spin spinning={saveFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CampaignCopyEntity>
                    name="CampaignCopyForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.DEFAULT}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
